import {DASHBOARD_ACCENT} from '#/lib/dashboardPalette'
import {useInvalidateWallet, useWalletQuery} from '#/state/queries/wallet'

export type TxType = 'debit' | 'credit' | 'platform_fee' | 'bill_purchase'

export interface Transaction {
  id: string
  type: TxType
  amount: number
  description: string
  sessionId: string | null
  counterpartyId: string | null
  timestamp: number
  signature: string
  balanceAfter: number
}

export interface WalletData {
  userId: string
  balance: number
  transactions: Transaction[]
}

export interface ChallengeParticipant {
  userId: string
  displayName: string
  avatarColor: string
  score: number
  coins: number
  completed: boolean
}

export interface ChallengeItem {
  id: string
  hostId: string
  status: 'waiting' | 'active' | 'completed' | 'cancelled'
  capsuleTitle: string
  subject: string
  coinsStake: number
  participants: ChallengeParticipant[]
}

export interface ChallengeStats {
  wins: number
  losses: number
  coinsWon: number
  coinsLost: number
  activeCount: number
}

export interface DashboardData {
  wallet: WalletData | null
  challenges: ChallengeItem[]
  weeklyAmounts: number[]
  sessionsHosted: number
  sessionsAttended: number
  totalSpent: number
  challengeStats: ChallengeStats
  loading: boolean
  error: string | null
  refetch: () => void
}

// Challenges API not yet implemented — keeping static data until backend is ready.
const DEMO_CHALLENGES: ChallengeItem[] = [
  {
    id: 'ch_1',
    hostId: 'host_1',
    status: 'completed',
    capsuleTitle: 'Dérivées et Intégrales',
    subject: 'Mathématiques',
    coinsStake: 10,
    participants: [
      {
        userId: 'demo',
        displayName: 'You',
        avatarColor: DASHBOARD_ACCENT.positive,
        score: 88,
        coins: 10,
        completed: true,
      },
      {
        userId: 'opp1',
        displayName: 'Sam',
        avatarColor: DASHBOARD_ACCENT.live,
        score: 72,
        coins: -10,
        completed: true,
      },
    ],
  },
  {
    id: 'ch_2',
    hostId: 'host_1',
    status: 'completed',
    capsuleTitle: 'Probabilités',
    subject: 'Mathématiques',
    coinsStake: 15,
    participants: [
      {
        userId: 'demo',
        displayName: 'You',
        avatarColor: DASHBOARD_ACCENT.positive,
        score: 91,
        coins: 15,
        completed: true,
      },
      {
        userId: 'opp2',
        displayName: 'Alex',
        avatarColor: DASHBOARD_ACCENT.primary,
        score: 65,
        coins: -15,
        completed: true,
      },
    ],
  },
  {
    id: 'ch_3',
    hostId: 'host_2',
    status: 'completed',
    capsuleTitle: 'Physique Quantique',
    subject: 'Physique',
    coinsStake: 20,
    participants: [
      {
        userId: 'demo',
        displayName: 'You',
        avatarColor: DASHBOARD_ACCENT.positive,
        score: 55,
        coins: -20,
        completed: true,
      },
      {
        userId: 'opp3',
        displayName: 'Marie',
        avatarColor: DASHBOARD_ACCENT.attention,
        score: 78,
        coins: 20,
        completed: true,
      },
    ],
  },
  {
    id: 'ch_4',
    hostId: 'host_1',
    status: 'active',
    capsuleTitle: 'Algèbre Linéaire',
    subject: 'Mathématiques',
    coinsStake: 10,
    participants: [
      {
        userId: 'demo',
        displayName: 'You',
        avatarColor: DASHBOARD_ACCENT.positive,
        score: 0,
        coins: 0,
        completed: false,
      },
      {
        userId: 'opp4',
        displayName: 'Lucas',
        avatarColor: DASHBOARD_ACCENT.primary,
        score: 0,
        coins: 0,
        completed: false,
      },
    ],
  },
]

function buildWeekly(txs: Transaction[], type: 'debit' | 'credit'): number[] {
  const buckets = [0, 0, 0, 0, 0, 0, 0]
  const now = Date.now()
  for (const tx of txs) {
    if (tx.type !== type) continue
    const daysAgoN = Math.floor((now - tx.timestamp) / 86_400_000)
    if (daysAgoN >= 0 && daysAgoN < 7) {
      buckets[6 - daysAgoN] += tx.amount
    }
  }
  return buckets
}

function buildChallengeStats(
  challenges: ChallengeItem[],
  userId: string,
): ChallengeStats {
  const completed = challenges.filter(c => c.status === 'completed')
  const wins = completed.filter(c => {
    const me = c.participants.find(p => p.userId === userId)
    const maxScore = Math.max(...c.participants.map(p => p.score))
    return me && me.score >= maxScore && c.participants.length > 1
  })
  const losses = completed.filter(c => !wins.includes(c))
  return {
    wins: wins.length,
    losses: losses.length,
    coinsWon: wins.reduce(
      (s, c) => s + (c.participants.find(p => p.userId === userId)?.coins ?? 0),
      0,
    ),
    coinsLost: losses.reduce((s, c) => s + c.coinsStake, 0),
    activeCount: challenges.filter(
      c => c.status === 'waiting' || c.status === 'active',
    ).length,
  }
}

export function useDashboardData(
  userId: string,
  role: 'student' | 'teacher',
): DashboardData {
  const isTeacher = role === 'teacher'
  const txType = isTeacher ? 'credit' : 'debit'

  const walletQuery = useWalletQuery()
  const invalidateWallet = useInvalidateWallet()
  const refetch = () => {
    void invalidateWallet()
  }

  const rawTxs = walletQuery.data?.transactions ?? []
  const txs: Transaction[] = rawTxs.map(tx => ({...tx}))

  const wallet: WalletData | null = walletQuery.data
    ? {
        userId,
        balance: walletQuery.data.balance,
        transactions: txs,
      }
    : null

  const uniqueSessionIds = new Set(
    txs.filter(t => t.sessionId).map(t => t.sessionId),
  )

  return {
    wallet,
    challenges: DEMO_CHALLENGES,
    weeklyAmounts: buildWeekly(txs, txType),
    sessionsHosted: isTeacher ? uniqueSessionIds.size : 0,
    sessionsAttended: isTeacher ? 0 : uniqueSessionIds.size,
    totalSpent: isTeacher
      ? 0
      : txs.filter(t => t.type === 'debit').reduce((s, t) => s + t.amount, 0),
    challengeStats: buildChallengeStats(DEMO_CHALLENGES, userId),
    loading: walletQuery.isLoading,
    error: walletQuery.error ? String(walletQuery.error) : null,
    refetch,
  }
}
